import { useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import SignupStep1 from "./SignupStep1";
import SignupStep2 from "./SignupStep2";
import SignupStep3 from "./SignupStep3";
import {
  Box,
  HeadingStyled,
  SpanStyled,
} from "../../../../components/styled/Elements";
import {
  BackArrow,
  BackArrowWithBg,
} from "../../../../components/styled/AllImages";
import { toast } from "react-toastify";
import AuthApi from "../../../../services/api/authApi";

const payment_method = {
  card: "card",
  bank_transfer: "bank_transfer",
};

const signupSteps = [
  {
    label: "signup_to_myoffice",
    component: SignupStep1,
    fields: [
      "name",
      "type",
      "organization_name",
      "email",
      "phone",
      "password",
      "confirm_password",
    ],
  },
  {
    label: "signup_to_myoffice",
    component: SignupStep2,
    fields: ["number_of_licenses", "payment_method"],
  },
  {
    label: "signup_to_myoffice",
    component: SignupStep3,
    fields: {
      [payment_method.card]: [
        "cardHolderName",
        "cardNumber",
        "expiryDate",
        "cvv",
      ],
      [payment_method.bank_transfer]: ["payment_proof"],
    },
  },
];

const SignupForm = ({ planInfo }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const {
    formState: { errors },
    register,
    unregister,
    handleSubmit,
    setValue,
    trigger,
    control,
    getFieldState,
    watch,
  } = useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const name = searchParams.get("name");
  const email = searchParams.get("email");
  const user_id = searchParams.get("user_id");
  const tap_customer_id = searchParams.get("tap_customer_id");
  const payment_method_param = searchParams.get("payment_method");
  const decoded_customer_id = atob(tap_customer_id);

  const isStepTwo = name && email && user_id && payment_method_param;

  const [promoDiscount, setPromoDiscount] = useState(0);
  const [user, setUser] = useState(
    isStepTwo
      ? {
          name,
          email,
          id: user_id,
          tap_customer_id: decoded_customer_id,
        }
      : null
  );
  const [currentStep, setCurrentStep] = useState(
    isStepTwo ? signupSteps[1] : signupSteps[0]
  );
  const [totalAmount, setTotalAmount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState(
    payment_method_param || "card"
  );
  const [bankInfo, setBankInfo] = useState({});
  const [planDetails, setPlanDetails] = useState({
    id: 1,
    amount: 0,
  });
  const currentStepIndex = signupSteps.indexOf(currentStep);

  const getFieldsToValidate = () => {
    const step = signupSteps[currentStepIndex];

    if (currentStepIndex < 2) {
      return step.fields;
    }
    return step.fields[paymentMethod] || [];
  };

  const validateCurrentStep = async () => {
    const fieldsToValidate = getFieldsToValidate();

    if (currentStepIndex === 2 && !paymentMethod) {
      return false;
    }

    const isValid = await trigger(fieldsToValidate);
    return isValid;
  };

  const handlePaymentMethodChange = (newMethod) => {
    const otherMethod =
      newMethod === "card" ? payment_method.bank_transfer : payment_method.card;

    // const fieldsToUnregister = signupSteps[2].fields[otherMethod];
    // unregister(fieldsToUnregister);

    setPaymentMethod(newMethod);
  };

  const handleSteps = async () => {
    const isCurrentStepValid = await validateCurrentStep();

    if (!isCurrentStepValid) {
      return;
    }

    if (currentStepIndex < 2) {
      setCurrentStep(signupSteps[currentStepIndex + 1]);
    }
  };

  const signUpStepTwo = async (userId) => {
    const obj = {
      user_id: userId,
      plan_id: planDetails?.id,
      price_per_license: planDetails?.amount || 0,
      sub_total: planDetails?.amount || 0,
      total_amount: planDetails?.amount || 0,
      plan_duration: planInfo?.duration,
      payment_method: "free",
      number_of_licenses: 100,

      discount_percentage: 0,
      discount_value: 0,
    };
    try {
      const { code, message } = await AuthApi.signupStepTwo(obj);
      if (code === 200) {
        toast.success(message);
        navigate("/login");
      }
      if (code === 100 || code === 204) {
        toast.error(message);
      }
    } catch (error) {}
  };

  const onSubmit = async (formData) => {
    const obj = {
      ...formData,
      plan_id: planDetails?.id,
    };
    try {
      setLoading(true);
      const response = await AuthApi.signupStepOne(obj);
      if (response.code === 200) {
        if (planDetails?.category == "free") {
          signUpStepTwo(response?.data?.user?.id);
        } else {
          setCurrentStep(signupSteps[1]);
        }
        setUser(response?.data?.user);
      }

      if (response?.data?.errors && response?.data?.errors?.email) {
        toast.error(response?.data?.errors?.email[0]);
      }

      if (response.code === 422 && response?.data?.data) {
        if (planDetails?.category == "free") {
          // signUpStepTwo(response?.data?.data?.user?.id);
        } else {
          setCurrentStep(signupSteps[1]);
        }
        setUser(response?.data?.data?.user);
      }
      if (response?.data?.errors?.phone[0] && !response?.data?.data) {
        toast.error(response?.data?.errors?.phone[0]);
      }
      if (
        response.code === 422 &&
        !response?.data?.data &&
        response?.data?.errors
      ) {
        toast.error(response?.data?.errors?.email[0]);
      }

      if (response.code === 100 || response.code === 204) {
        toast.error(response.message);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };
  const getBankInfo = async () => {
    try {
      const { data, code } = await AuthApi.getBankInfo();
      if (code === 200) {
        setBankInfo(data);
      }
    } catch (error) {}
  };

  useEffect(() => {
    getBankInfo();
  }, [language]);

  const getPlanInfo = async (id, type) => {
    try {
      const { data, code } = await AuthApi.getPlanInfo(id, type);
      if (code === 200) {
        setPlanDetails({
          ...data,
          amount:
            data?.prices?.find((price) => price?.duration == planInfo?.duration)
              ?.price || "",
          duration: planInfo?.duration || 1,
        });
      } else {
        setPlanDetails({
          ...planInfo,
          id: planInfo?.id,
          duration: planInfo?.duration || 1,
        });
      }
    } catch (error) {}
  };
  useEffect(() => {
    if (planInfo?.id) {
      getPlanInfo(planInfo?.id, planInfo?.plan_type);
    }
  }, [planInfo]);

  const StepComponent = currentStep.component;

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <Box className="mb-4">
        <HeadingStyled
          size="24px"
          weight="bold"
          color="var(--text)"
          className="d-flex align-items-center gap-3"
        >
          <SpanStyled className="mb-1">
            <BackArrowWithBg
              className="back-btn cursor-pointer"
              onClick={() => {
                if (currentStepIndex > 0)
                  setCurrentStep(signupSteps[currentStepIndex - 1]);
                else window.history.back();
              }}
            />
          </SpanStyled>

          {t(currentStep.label)}
        </HeadingStyled>
      </Box>
      <StepComponent
        user={user}
        planInfo={planDetails}
        paymentMethod={paymentMethod}
        bankInfo={bankInfo}
        totalAmount={totalAmount}
        setTotalAmount={setTotalAmount}
        register={register}
        errors={errors}
        getFieldState={getFieldState}
        control={control}
        watch={watch}
        setValue={setValue}
        loading={loading}
        handleSteps={handleSteps}
        setPaymentMethod={handlePaymentMethodChange}
        validateCurrentStep={validateCurrentStep}
        promoDiscount={promoDiscount}
        setPromoDiscount={setPromoDiscount}
      />
    </Form>
  );
};

export default SignupForm;
